// openEHR BASE model — BMM package org.openehr.base.foundation_types.terminology
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { TYPE_REGISTRY } from "../_shared.ts";
import { TERMINOLOGY_ID } from "../base_types/identification.ts";
import { applyRmInit, type RmInit } from "../apply_rm_init.ts";
import { Any } from "./foundation_types.ts";
import { matchTerseCodePhrase } from "../../serialization/terse_strings.ts";
import { Boolean, String } from "./primitive_types.ts";
import type { Uri } from "./primitive_types.ts";

/**
 * Primitive type representing a standalone reference to a terminology concept, in the form of a terminology identifier, optional version, and a code or code string from the terminology.
 */
export class Terminology_code extends Any {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("TERMINOLOGY_CODE", Terminology_code);
  }

  /**
   * Internal storage for terminology_id
   * @protected
   */
  protected _terminology_id?: String;

  /**
   * The archetype environment namespace identifier used to identify a terminology. Typically a value like \`"snomed_ct"\` that is mapped elsewhere to the full URI identifying the terminology.
   */
  get terminology_id(): string | undefined {
    return this._terminology_id?.value;
  }

  /**
   * Gets the String wrapper object for terminology_id.
   * Use this to access String methods.
   */
  get $terminology_id(): String | undefined {
    return this._terminology_id;
  }

  /**
   * Sets terminology_id from either a primitive value or String wrapper.
   */
  set terminology_id(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._terminology_id = undefined;
    } else if (typeof val === "string") {
      this._terminology_id = String.from(val);
    } else {
      this._terminology_id = val;
    }
  }

  /**
   * Internal storage for terminology_version
   * @protected
   */
  protected _terminology_version?: String;

  /**
   * Optional string value representing terminology version, typically a date or dotted numeric.
   */
  get terminology_version(): string | undefined {
    return this._terminology_version?.value;
  }

  /**
   * Gets the String wrapper object for terminology_version.
   * Use this to access String methods.
   */
  get $terminology_version(): String | undefined {
    return this._terminology_version;
  }

  /**
   * Sets terminology_version from either a primitive value or String wrapper.
   */
  set terminology_version(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._terminology_version = undefined;
    } else if (typeof val === "string") {
      this._terminology_version = String.from(val);
    } else {
      this._terminology_version = val;
    }
  }

  /**
   * Internal storage for code_string
   * @protected
   */
  protected _code_string?: String;

  /**
   * A terminology code or post-coordinated code expression, if supported by the terminology. The code may refer to a single term, a value set consisting of multiple terms, or some other entity representable within the terminology.
   */
  get code_string(): string | undefined {
    return this._code_string?.value;
  }

  /**
   * Gets the String wrapper object for code_string.
   * Use this to access String methods.
   */
  get $code_string(): String | undefined {
    return this._code_string;
  }

  /**
   * Sets code_string from either a primitive value or String wrapper.
   */
  set code_string(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._code_string = undefined;
    } else if (typeof val === "string") {
      this._code_string = String.from(val);
    } else {
      this._code_string = val;
    }
  }

  /**
   * The URI reference that may be used as a concrete key into a notional terminology service for queries that can obtain the term text, definition, and other associated elements.
   */
  uri?: Uri;

  /**
   * Value equality: return True if this and other are equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (!(other instanceof Terminology_code)) {
      return new Boolean(false);
    }
    
    // Compare terminology_id
    const termIdMatch = (this.terminology_id === other.terminology_id) ||
      (this._terminology_id !== undefined && other._terminology_id !== undefined &&
       this._terminology_id.is_equal(other._terminology_id).value);
    
    // Compare code_string
    const codeMatch = (this.code_string === other.code_string) ||
      (this._code_string !== undefined && other._code_string !== undefined &&
       this._code_string.is_equal(other._code_string).value);
    
    // Compare terminology_version (optional)
    const versionMatch = (this.terminology_version === other.terminology_version) ||
      (this._terminology_version === undefined && other._terminology_version === undefined) ||
      (this._terminology_version !== undefined && other._terminology_version !== undefined &&
       this._terminology_version.is_equal(other._terminology_version).value);
    
    return new Boolean(termIdMatch && codeMatch && versionMatch);
  }
}

/**
 * Leaf type representing a standalone term from a terminology, which consists of the term text and the code, i.e. a concept reference.
 */
export class Terminology_term extends Any {
  // Lazy type registration - only runs when class is actually used
  static {
    TYPE_REGISTRY.set("TERMINOLOGY_TERM", Terminology_term);
  }

  /**
   * Reference to the terminology concept formally representing this term.
   */
  concept?: Terminology_code;
  /**
   * Internal storage for text
   * @protected
   */
  protected _text?: String;

  /**
   * Text of term.
   */
  get text(): string | undefined {
    return this._text?.value;
  }

  /**
   * Gets the String wrapper object for text.
   * Use this to access String methods.
   */
  get $text(): String | undefined {
    return this._text;
  }

  /**
   * Sets text from either a primitive value or String wrapper.
   */
  set text(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._text = undefined;
    } else if (typeof val === "string") {
      this._text = String.from(val);
    } else {
      this._text = val;
    }
  }

  /**
   * Value equality: return True if this and other are equal in value.
   * @param other - Parameter
   * @returns Result value
   */
  override is_equal(other: Any): Boolean {
    if (!(other instanceof Terminology_term)) {
      return new Boolean(false);
    }
    
    // Compare text
    const textMatch = (this.text === other.text) ||
      (this._text !== undefined && other._text !== undefined &&
       this._text.is_equal(other._text).value);
    
    // Compare concept
    const conceptMatch = (this.concept === undefined && other.concept === undefined) ||
      (this.concept !== undefined && other.concept !== undefined &&
       this.concept.is_equal(other.concept).value);
    
    return new Boolean(textMatch && conceptMatch);
  }
}

/**
 * A fully coordinated (i.e. all coordination has been performed) term from a terminology service (as distinct from a particular terminology).
 *
 * Retain for LEGACY only, while ADL1.4 requires CODE_PHRASE.
 *
 * SPECAM-82 moved this class from `foundation_types.terminology` to
 * `base_types.terminology` in the BMM package map; the TypeScript module
 * path is unchanged so existing imports keep working.
 */
export class CODE_PHRASE {
  /**
   * Object init matches the RM `CODE_PHRASE` constructor. The TypeScript
   * serializer imports this BASE class. A terse `terminology::code` string
   * is accepted too. `undefined` leaves the instance empty.
   */
  constructor(init?: string | RmInit) {
    if (typeof init === "string") {
      // Same parse as init_helpers `initCodePhrase`, without importing it
      // (init_helpers imports the RM, which imports this module).
      const parts = matchTerseCodePhrase(init);
      if (!parts) {
        throw new Error(
          `Invalid CODE_PHRASE format: "${init}"\nExpected terse format like "terminology::code"`,
        );
      }
      const terminologyId = new TERMINOLOGY_ID();
      terminologyId.value = parts.termId;
      this.terminology_id = terminologyId;
      this.code_string = parts.code;
      return;
    }
    applyRmInit(this, init);
  }

  /**
   * Identifier of the distinct terminology from which the code_string (or its elements) was extracted.
   */
  terminology_id?: TERMINOLOGY_ID;
  /**
   * Internal storage for code_string
   * @protected
   */
  protected _code_string?: String;

  /**
   * The key used by the terminology service to identify a concept or coordination of concepts. This string is most likely parsable inside the terminology service, but nothing can be assumed about its syntax outside that context.
   */
  get code_string(): string | undefined {
    return this._code_string?.value;
  }

  /**
   * Gets the String wrapper object for code_string.
   * Use this to access String methods.
   */
  get $code_string(): String | undefined {
    return this._code_string;
  }

  /**
   * Sets code_string from either a primitive value or String wrapper.
   */
  set code_string(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._code_string = undefined;
    } else if (typeof val === "string") {
      this._code_string = String.from(val);
    } else {
      this._code_string = val;
    }
  }

  /**
   * Internal storage for preferred_term
   * @protected
   */
  protected _preferred_term?: String;

  /**
   * Optional attribute to carry preferred term corresponding to the code or expression in \`_code_string_\`. Typical use in integration situations which create mappings, and representing data for which both a (non-preferred) actual term and a preferred term are both required.
   */
  get preferred_term(): string | undefined {
    return this._preferred_term?.value;
  }

  /**
   * Gets the String wrapper object for preferred_term.
   * Use this to access String methods.
   */
  get $preferred_term(): String | undefined {
    return this._preferred_term;
  }

  /**
   * Sets preferred_term from either a primitive value or String wrapper.
   */
  set preferred_term(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._preferred_term = undefined;
    } else if (typeof val === "string") {
      this._preferred_term = String.from(val);
    } else {
      this._preferred_term = val;
    }
  }

  /**
   * Factory method to create a CODE_PHRASE.
   * @param terminologyId - The terminology identifier
   * @param codeString - The code string
   * @returns A new CODE_PHRASE instance
   */
  static from(terminologyId: string, codeString: string): CODE_PHRASE {
    const codePhrase = new CODE_PHRASE();

    const termId = new TERMINOLOGY_ID();
    termId.value = terminologyId;
    codePhrase.terminology_id = termId;

    codePhrase.code_string = codeString;

    return codePhrase;
  }

  /**
   * Compare two CODE_PHRASE objects for equality.
   * @param other - The other object to compare with
   * @returns Boolean indicating if they are equal
   */
  is_equal(other: any): Boolean {
    if (!(other instanceof CODE_PHRASE)) {
      return new Boolean(false);
    }

    // Compare terminology IDs
    if (!this.terminology_id || !other.terminology_id) {
      return new Boolean(this.terminology_id === other.terminology_id);
    }
    if (this.terminology_id.value !== other.terminology_id.value) {
      return new Boolean(false);
    }

    // Compare code strings
    if (this.code_string !== other.code_string) {
      return new Boolean(false);
    }

    return new Boolean(true);
  }
}
